/* eslint-disable max-lines */
import { ScopeType, Show, VideoOnDemandStatus } from 'data/Show'
import {
    showHasEnded,
    showIsSoldOut,
    showHasStarted,
    getPreviewEndTime,
    shouldStartPreview,
    getMSBeforeShowIntroduction,
    isBeforeShowIntro,
    getMSBeforeShowStartTime,
} from 'utils/show'
import { isNil } from 'lodash'
import { showErrorBanner, resetState as resetMessagingBanner } from 'redux/slices/messagingBanner'
import {
    onUserPurchasedPassMsg,
    onShowVODStateMsg,
    onShowSoldOutMsg,
    onBuyPassToEnterTheShowMsg,
    onFreePreviewEndedMsg,
    onFreePreviewStartedMsg,
    onRedeemOnlyMsg,
    onUserAccessPrivateEvent,
} from 'redux/slices/messagingBanner/thunks'
import moment from 'moment'
import { fetchShowVideo, postTribute } from 'api/api'
import * as api from 'api'
import {
    fetchViewerBreakdown,
    getShowViewerStartTime,
    resetState as resetViewer,
    registerViewer,
} from 'redux/slices/viewer'
import { setInitialPaywallState, provideAccess, resetState as resetPaywall } from 'redux/slices/paywall'
import { userHasPurchasedTicket } from 'redux/slices/passes'
import { retrieveUserShowPasses } from 'redux/slices/passes/thunks'
import {
    retrieveForum,
    subscribeToShowChannel,
    openChannel,
    getShowChannel,
    closeShowChannel,
    resetState as resetInteraction,
} from 'redux/slices/interaction'
import { resetState as resetLiveUserActions } from 'redux/slices/liveUserActions'
import { retrieveUserCustomLuas } from 'redux/slices/liveUserActions/thunks'
import { notesReceived } from 'redux/slices/showNotes'
import { resetState as resetPassTier } from 'redux/slices/passTier'
import { initiatePassTiers } from 'redux/slices/passTier/thunks'
import {
    showReceived,
    videoReceived,
    sendingTribute,
    sendTributeResponse,
    setInitialVideoOnDemand,
    getRemainingPasses,
    updateRemainingPasses,
    showStarted,
    previewStarted,
    previewEnded,
    getShow,
    getVideo,
    forceVideoToLive,
    getWillForceVideoToLive,
    willForceVideoToLive,
    resetState as resetLiveShow,
    getShowIsSoldOut,
    passTiersReceived,
} from '.'
import { isFailure } from 'data/ApiResult'
import { handleApiError } from 'redux/slices/error'
import { VideoStatus, Video } from 'data/Video'
import { isLiveStreamActive, isLiveStreamIdle } from 'utils/video'
import { AppThunk } from 'redux/store'
import { getTimer, Timer } from 'redux/slices/timers'
import { clearTimeoutFromStore, createTimer } from 'redux/slices/timers/thunks'
import { resetState as resetChat } from 'redux/slices/chat'
import { resetState as resetGlobalError } from 'redux/slices/globalError'
import { resetState as resetPayment } from 'redux/slices/payment'
import { isPopulated, isPopulatedArray } from 'utils/populated'
import { CLOSE_CURTAIN_AFTER_MINUTES, CURTAIN_ANIMATION_IN_SECONDS } from 'constants.js'
import { isCurtainOpen } from 'utils/curtain'
import { openCurtain, closeCurtainWithEndedState, closeCurtainWithArmageddonState } from 'redux/slices/curtain/thunks'
import { getCurtainValue, resetState as resetCurtain, unlockCurtain } from 'redux/slices/curtain'
import { createAsyncThunk } from '@reduxjs/toolkit'

type RemainingPasses = number | null

export const setTimeOutToForceLive = (): AppThunk => async (dispatch, getState) => {
    const show = getShow(getState())
    const willForceLive = getWillForceVideoToLive(getState())

    if (!show) return

    if (!willForceLive) {
        dispatch(willForceVideoToLive())

        const time = getMSBeforeShowIntroduction(show.startTime)

        dispatch(
            createTimer(Timer.SET_PAGE_FORCE_LIVE, time, () => {
                const video = getVideo(getState())
                if (isLiveStreamActive(video)) {
                    dispatch(forceVideoToLive())
                }
            }),
        )
    }
}

export const updateVideoStatus = (video: { status?: VideoStatus; updatedAt?: string }): AppThunk => async (
    dispatch,
    getState,
) => {
    const prevVideoStatus = getVideo(getState())
    const show = getShow(getState())
    const forceToLiveTimeoutId = getTimer(getState(), Timer.SET_PAGE_FORCE_LIVE)

    const newVideoStatus = {
        status: video.status || VideoStatus.ERROR,
        updatedAt: video.updatedAt || moment().format(),
    }

    dispatch(videoReceived(newVideoStatus))

    dispatch(openOrCloseTheCurtain({ show: show!, video: newVideoStatus }))

    if (
        show &&
        !forceToLiveTimeoutId &&
        isBeforeShowIntro(show.startTime) &&
        !isLiveStreamActive(prevVideoStatus) &&
        isLiveStreamActive(newVideoStatus)
    ) {
        dispatch(setTimeOutToForceLive())
    }
}

export const openOrCloseTheCurtain = createAsyncThunk(
    'liveShow/openOrCloseTheCurtain',
    async ({ show, video }: { show: Show; video: Video }, { dispatch, getState }: any) => {
        const closeCurtainTimerId = getTimer(getState(), Timer.SET_PAGE_CLOSE_CURTAIN)

        const initialTimeToAllowClosingTheCurtain = moment(show.startTime).add(CLOSE_CURTAIN_AFTER_MINUTES, 'minutes')

        const canStartClosingTheCurtain = moment().isSameOrAfter(initialTimeToAllowClosingTheCurtain)
        const videoIsIdle = isLiveStreamIdle(video)

        if (showHasEnded(show)) {
            dispatch(openCurtain([Timer.SET_PAGE_CLOSING_CURTAIN, Timer.SET_PAGE_CLOSE_CURTAIN]))
            return
        }

        if (videoIsIdle && canStartClosingTheCurtain) {
            dispatch(closeCurtainWithEndedState())
        } else if (videoIsIdle && !canStartClosingTheCurtain) {
            if (!closeCurtainTimerId) {
                const curtainCloseStateTime = moment(show.startTime).add(CLOSE_CURTAIN_AFTER_MINUTES, 'minutes')
                const time = moment(curtainCloseStateTime).diff(moment(), 'milliseconds')
                dispatch(
                    createTimer(Timer.SET_PAGE_CLOSE_CURTAIN, time, () => {
                        const curtainValue = getCurtainValue(getState())
                        const video = getVideo(getState())
                        if (isCurtainOpen(curtainValue) && isLiveStreamIdle(video)) {
                            dispatch(closeCurtainWithEndedState())
                        }
                    }),
                )
            }
            dispatch(openCurtain())
        } else if (!videoIsIdle && canStartClosingTheCurtain) {
            dispatch(openCurtain([Timer.SET_PAGE_CLOSING_CURTAIN]))
        } else {
            dispatch(openCurtain([Timer.SET_PAGE_CLOSE_CURTAIN]))
        }
    },
    {
        condition: (_, { getState }: any) => {
            return !isNil(getShow(getState()))
        },
    },
)

export const retrieveVideo = (showId: string): AppThunk => async dispatch => {
    try {
        const video = await fetchShowVideo(showId)
        dispatch(updateVideoStatus(video))
    } catch (err) {
        dispatch(updateVideoStatus({ status: VideoStatus.ERROR }))
        dispatch(showErrorBanner({ message: "Uh oh! We're experiencing connectivity issues. Please refresh the page" }))
    }
}

export const retrieveLiveData = createAsyncThunk('liveShow/retrieveLiveData', async (path: string, thunkAPI: any) => {
    const { dispatch, getState } = thunkAPI
    try {
        await dispatch(retrieveAndStoreShow(path))

        const show = getShow(getState())

        if (!show) {
            return Promise.reject()
        }

        await dispatch(retrieveAndInitiateFollowingData())

        if (isPopulatedArray(show.luaPassTiers)) {
            await dispatch(retrieveUserCustomLuas(show.luaPassTiers, show._id))
        }

        dispatch(initiatePassTiers())

        const subscribedChannel = await dispatch(subscribeToShowChannel(show))
        await dispatch(registerViewer(show, subscribedChannel))
        await dispatch(initiateShow(show))
    } catch (e) {}
})

export const connectToChat = (path: string): AppThunk => async dispatch => {
    const result = await api.getShow(path)

    if (isFailure(result)) {
        console.error('We were not able to connect to the chat')
        return
    }

    const show = result.data

    try {
        if (isPopulatedArray(show.luaPassTiers)) {
            await dispatch(retrieveUserCustomLuas(show.luaPassTiers, show._id))
        }

        await dispatch(openChannel(show.channelName))

        await dispatch(showReceived(show))
    } catch (error) {
        console.error(`Unable to finish connecting to chat`)
    }
}

export const connectToForum = (path: string): AppThunk => async dispatch => {
    const result = await api.getShow(path)

    if (isFailure(result)) {
        console.error('We were not able to connect to the chat')
        return
    }

    const show = result.data

    try {
        await dispatch(openChannel(show.channelName))

        await dispatch(showReceived(show))

        await dispatch(retrieveForum(show._id))
    } catch (error) {
        console.error(`Unable to finish connecting to forum`)
    }
}

export const retrieveAndStoreShow = (path: string) => async dispatch => {
    const result = await api.getShow(path)

    if (isFailure(result)) {
        return dispatch(handleApiError(result))
    }

    const show = result.data

    dispatch(showReceived(show))

    if (isPopulatedArray(show.passTiers)) {
        dispatch(passTiersReceived(show.passTiers))
    }

    if (show.hypeVideoVOD && isPopulated(show.hypeVideoVOD) && show.hypeVideoVOD.status === VideoOnDemandStatus.READY) {
        await dispatch(setInitialVideoOnDemand(show.hypeVideoVOD))
    }
}

export const initiateShow = (show: Show) => async (dispatch, getState) => {
    if (showHasEnded(show)) {
        dispatch(provideAccess())
        return
    }

    if (showHasStarted(show)) {
        dispatch(showStarted())

        if (show.scope === ScopeType.PUBLIC) {
            dispatch(startOrFinishPreview())
        }
    } else {
        const timeToStart = getMSBeforeShowStartTime(show.startTime)

        dispatch(
            createTimer(Timer.SET_PAGE_START_TIME, timeToStart, () => {
                const video = getVideo(getState())

                dispatch(showStarted())

                if (show.scope === ScopeType.PUBLIC) {
                    const userPurchasedTicket = userHasPurchasedTicket(getState())

                    if (!userPurchasedTicket) {
                        dispatch(startPreview())
                    }
                }

                if (!isLiveStreamActive(video)) {
                    dispatch(retrieveVideo(show._id))
                }
            }),
        )
    }
}

export const startOrFinishPreview = (): AppThunk => async (dispatch, getState) => {
    const show = getShow(getState())!
    const userPurchasedTicket = userHasPurchasedTicket(getState())

    const previewStartedAt = getShowViewerStartTime(getState())
    const showIsSoldOut = getShowIsSoldOut(getState())
    const showEnded = showHasEnded(show)

    if (userPurchasedTicket || showEnded || showIsSoldOut) {
        return
    }

    if (shouldStartPreview(previewStartedAt)) {
        dispatch(startPreview())
    } else if (moment().isSameOrAfter(getPreviewEndTime(previewStartedAt))) {
        dispatch(endPreview())
    }
}

export const retrieveAndInitiateFollowingData = (): AppThunk => async (dispatch, getState) => {
    const show = getShow(getState())!

    await dispatch(retrieveUserShowPasses(show._id))

    await Promise.all([
        dispatch(retrieveVideo(show._id)),
        dispatch(setInitialPaywallState(show._id)),
        dispatch(retrieveForum(show._id)),
    ])

    if (show.scope === ScopeType.PUBLIC) {
        if (showHasEnded(show)) {
            dispatch(onShowVODStateMsg())
        } else if (userHasPurchasedTicket(getState())) {
            dispatch(onUserPurchasedPassMsg())
        } else if (showIsSoldOut(getRemainingPasses(getState()))) {
            dispatch(onShowSoldOutMsg())
        } else if (show.disablePaywall) {
            dispatch(onRedeemOnlyMsg())
        } else {
            dispatch(onBuyPassToEnterTheShowMsg())
        }
    } else {
        if (showHasEnded(show)) {
            dispatch(onShowVODStateMsg())
        } else if (userHasPurchasedTicket(getState())) {
            dispatch(onUserAccessPrivateEvent())
        } else if (showIsSoldOut(getRemainingPasses(getState()))) {
            dispatch(onShowSoldOutMsg())
        } else {
            dispatch(onUserAccessPrivateEvent())
        }
    }
}

export const refreshUserAccessState = (): AppThunk => async (dispatch, getState) => {
    const show = getShow(getState())

    if (!show) {
        return
    }

    await dispatch(retrieveUserShowPasses(show._id))

    const hasTicket = userHasPurchasedTicket(getState())

    if (hasTicket) {
        await dispatch(setInitialPaywallState(show._id))
    }
}

export const sendTributeToArtist = (body): AppThunk => async dispatch => {
    dispatch(sendingTribute())
    try {
        await postTribute(body)
        dispatch(
            sendTributeResponse({
                result: 'success',
                message: 'Successfully submitted!',
            }),
        )
    } catch (error) {
        dispatch(
            sendTributeResponse({
                result: 'error',
                message: 'Error encountered, please try again. If the problem persists please contact us.',
            }),
        )
    }
}

export const retrieveArtistShow = (id: string) => async (dispatch, getState) => {
    const result = await api.getArtistShow(id)

    if (isFailure(result)) {
        return dispatch(handleApiError(result))
    }

    const show = result.data

    dispatch(fetchViewerBreakdown(id))
    dispatch(showReceived(show))

    if (isPopulatedArray(show.luaPassTiers)) {
        dispatch(retrieveUserCustomLuas(show.luaPassTiers, show._id))
    }

    if (isPopulatedArray(show.notes)) {
        dispatch(notesReceived(show.notes))
    }

    if (!getShowChannel(getState())) {
        await dispatch(openChannel(show.channelName))
    }

    return show
}

export const updateRemainingPassesAction = (remainingPasses: RemainingPasses): AppThunk => async (
    dispatch,
    getState,
) => {
    dispatch(updateRemainingPasses(remainingPasses))

    if (showIsSoldOut(remainingPasses) && !userHasPurchasedTicket(getState())) {
        dispatch(onShowSoldOutMsg())
    }
}

export const startPreview = (): AppThunk => async (dispatch, getState) => {
    const previewTimeoutId = getTimer(getState(), Timer.SET_PAGE_END_PREVIEW)
    const previewStartAt = getShowViewerStartTime(getState())

    const now = moment()
    const previewEndTime = getPreviewEndTime(previewStartAt)
    const time = moment(previewEndTime).diff(now)

    if (!previewTimeoutId) {
        dispatch(
            createTimer(Timer.SET_PAGE_END_PREVIEW, time, () => {
                dispatch(endPreview())
            }),
        )
    }

    dispatch(previewStarted(previewEndTime))
    dispatch(onFreePreviewStartedMsg())
    window.heap.track('Free Preview Started')
}

export const endPreview = (): AppThunk => async dispatch => {
    window.heap.track('Free Preview Ended')
    dispatch(previewEnded())
    dispatch(onFreePreviewEndedMsg())
}

export const onPlayerEnds = (): AppThunk => async (dispatch, getState) => {
    try {
        dispatch(
            videoReceived({
                status: VideoStatus.IDLE,
                updatedAt: moment().format(),
            }),
        )

        const show = getShow(getState())
        await dispatch(retrieveVideo(show!._id))
        const video = getVideo(getState())

        if (video.status === VideoStatus.ACTIVE) {
            dispatch(
                videoReceived({
                    status: VideoStatus.ACTIVE,
                    updatedAt: moment().format(),
                }),
            )
        } else {
            dispatch(closeCurtainWithEndedState())
        }
    } catch (e) {
        // In case we have an issue fetching the video status let's just close the curtain
        dispatch(closeCurtainWithEndedState())
    }
}
/*
 * Huston we have a problem! 🧨
 * In a worst case scenario if something goes awry during a live stream (for example a power outage on site), we need to have a good UX with messaging explaining that there are technical difficulties.
 * This will be known as Code Armageddon.
 */
export const initiateProtocolArmageddon = (payload: { message?: string }): AppThunk => async dispatch => {
    dispatch(unlockCurtain())
    dispatch(openCurtain())
    dispatch(
        createTimer(Timer.SET_PAGE_OPEN_CURTAIN, CURTAIN_ANIMATION_IN_SECONDS * 1000, () => {
            dispatch(closeCurtainWithArmageddonState(payload.message))
        }),
    )
}

/*
 * All Good! We can go back to normality.
 * This will deactivate the Code Armageddon.
 */
export const deactivateProtocolArmageddon = (): AppThunk => async dispatch => {
    dispatch(unlockCurtain())
    dispatch(openCurtain())
}

export const setPageLeave = (): AppThunk => async (dispatch, getState) => {
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_FORCE_LIVE))
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_END_PREVIEW))
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_START_TIME))
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_OPEN_CURTAIN))
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_CLOSE_CURTAIN))
    dispatch(clearTimeoutFromStore(Timer.SET_PAGE_CLOSING_CURTAIN))
    dispatch(clearTimeoutFromStore(Timer.WORDFLOW_TIME_LEFT))

    const show = getShow(getState())

    show && dispatch(closeShowChannel(show.channelName))

    dispatch(resetLiveShow())
    dispatch(resetMessagingBanner())
    dispatch(resetChat())
    dispatch(resetGlobalError())
    dispatch(resetInteraction())
    dispatch(resetLiveUserActions())
    dispatch(resetPassTier())
    dispatch(resetPaywall())
    dispatch(resetViewer())
    dispatch(resetPayment())
    dispatch(resetCurtain())
}
